import { useState } from 'react';
import { type RowData, type SortingState } from '@tanstack/react-table';

import type { DataTableComponentProps } from '../types';

import { useAppTable } from '../table';

import { DataTablePagination } from './DataTablePagination';
import { TableContent } from './TableContent';

export function DataTable<TData extends RowData>(
  props: DataTableComponentProps<TData>,
) {
  const {
    data,
    columns,
    pending = false,
    emptyMessage,
    loadingMessage,
    onRowClick,
    getRowClassName,
    pagination,
    sorting: externalSorting,
    onSortingChange: propOnSortingChange,
    renderSubRow,
    dnd,
    emptyState,
  } = props;

  const [internalSorting, setInternalSorting] = useState<SortingState>([]);

  const sorting = externalSorting ?? internalSorting;

  const onSortingChange = (
    updater: SortingState | ((old: SortingState) => SortingState),
  ) => {
    const newSorting =
      typeof updater === 'function' ? updater(sorting) : updater;

    if (!externalSorting) {
      setInternalSorting(newSorting);
    }

    propOnSortingChange?.(newSorting);
  };

  const table = useAppTable({
    data,
    columns,
    manualSorting: true,
    getRowCanExpand: () => Boolean(renderSubRow),
    state: {
      sorting,
    },
    onSortingChange,
  });

  return (
    <div className="flex flex-col gap-3">
      <TableContent
        table={table}
        columns={columns}
        pending={pending}
        emptyMessage={emptyMessage}
        loadingMessage={loadingMessage}
        onRowClick={onRowClick}
        getRowClassName={getRowClassName}
        renderSubRow={renderSubRow}
        dnd={dnd}
        emptyState={emptyState}
      />
      {pagination && !pending && (
        <DataTablePagination
          page={pagination.page}
          sizePerPage={pagination.sizePerPage}
          recordsTotal={pagination.recordsTotal}
          onPageChange={pagination.onPageChange}
          onSizePerPageChange={pagination.onSizePerPageChange}
          pageSizes={pagination.pageSizeOptions}
        />
      )}
    </div>
  );
}
